import api from './api';

/**
 * Team Service
 * Handles team management operations on the client side
 */
const teamService = {
  /**
   * Create a new team
   * @param {Object} teamData - Team data
   * @returns {Promise<Object>} - The created team
   */
  createTeam: async (teamData) => {
    const response = await api.post('/api/teams', teamData);
    return response.data;
  },

  /**
   * Get a team by ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} - The team
   */
  getTeamById: async (teamId) => {
    try {
      console.log(`Fetching team with ID: ${teamId}`);
      const response = await api.get(`/api/teams/${teamId}`);
      console.log('Team data received:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching team with ID ${teamId}:`, error);
      if (error.response) {
        console.error('API error response:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Get all teams for the current user
   * @returns {Promise<Array>} - List of teams
   */
  getUserTeams: async () => {
    const response = await api.get('/api/teams');
    return response.data;
  },

  /**
   * Update a team
   * @param {string} teamId - Team ID
   * @param {Object} teamData - Updated team data
   * @returns {Promise<Object>} - The updated team
   */
  updateTeam: async (teamId, teamData) => {
    try {
      console.log(`Updating team with ID: ${teamId}`, teamData);
      const response = await api.put(`/api/teams/${teamId}`, teamData);
      console.log('Team update response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating team with ID ${teamId}:`, error);
      if (error.response) {
        console.error('API error response:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Add a user to a team
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to add
   * @param {string} role - Role to assign (default: 'member')
   * @returns {Promise<Object>} - The updated team
   */
  addTeamMember: async (teamId, userId, role = 'member') => {
    const response = await api.post(`/api/teams/${teamId}/members`, { userId, role });
    return response.data;
  },

  /**
   * Remove a user from a team
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to remove
   * @returns {Promise<Object>} - The updated team
   */
  removeTeamMember: async (teamId, userId) => {
    const response = await api.delete(`/api/teams/${teamId}/members/${userId}`);
    return response.data;
  },

  /**
   * Add a project to a team
   * @param {string} teamId - Team ID
   * @param {string} projectId - Project ID to add
   * @returns {Promise<Object>} - The updated team
   */
  addTeamProject: async (teamId, projectId) => {
    const response = await api.post(`/api/teams/${teamId}/projects`, { projectId });
    return response.data;
  },

  /**
   * Remove a project from a team
   * @param {string} teamId - Team ID
   * @param {string} projectId - Project ID to remove
   * @returns {Promise<Object>} - The updated team
   */
  removeTeamProject: async (teamId, projectId) => {
    const response = await api.delete(`/api/teams/${teamId}/projects/${projectId}`);
    return response.data;
  },

  /**
   * Archive a team
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} - The updated team
   */
  archiveTeam: async (teamId) => {
    const response = await api.put(`/api/teams/${teamId}/archive`);
    return response.data;
  }
};

export default teamService;